import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../lib/i18n';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { emailReviewDocument, emailReviewKey, emailReviewMessage, emailReviewPageUrl, reviewNonce } from '../../../lib/email-review';
import { useEmailCampaignPreview } from '../../../lib/email-review-api';
import { closeEmailReview, openEmailReview, useEmailReviewFlow, type EmailReviewFlow, type EmailReviewSendResult } from '../../../lib/email-review-store';
import { Button, Typography } from '../ui';
import { SiteReviewFrame, type ReviewDocument } from './SiteReviewFrame';
import styles from './SiteEditHost.module.css';
import own from './EmailReviewHost.module.css';

export function EmailReviewHost() {
  const flow = useEmailReviewFlow();
  if (!flow || flow.beside || typeof document === 'undefined') return null;
  return <EmailReviewSheet flow={flow} />;
}

export function EmailReviewPane({ flow }: { flow: EmailReviewFlow }) {
  return <EmailReview flow={flow} layout="pane" />;
}

function EmailReviewSheet({ flow }: { flow: EmailReviewFlow }) {
  const { t } = useTranslation();
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  return createPortal(
    <div className={styles.sheet} role="dialog" aria-modal="true" aria-label={flow.card.subject || t('emailReview.title')} data-testid="emailReviewSheet">
      <EmailReview flow={flow} layout="sheet" />
    </div>,
    document.body,
  );
}

function EmailReview({ flow, layout }: { flow: EmailReviewFlow; layout: 'pane' | 'sheet' }) {
  const { t } = useTranslation();
  const openSideChat = useOpenSideChat();
  const { logPageClickBi } = useGenericBi();
  const { card, workspaceId, conversationId } = flow;
  const [refused, setRefused] = useState<Exclude<EmailReviewSendResult, { status: 'sent' }> | null>(null);
  const preview = useEmailCampaignPreview({ workspaceId, metaSiteId: card.metaSiteId, campaignId: card.campaignId });
  const pageUrl = emailReviewPageUrl(card.campaignId);
  const subject = preview.data?.subject || card.subject;
  const title = subject || t('emailReview.title');
  const loading = t('emailReview.loading');
  const unavailable = t('emailReview.unavailable');
  const reviewDocument = useMemo<ReviewDocument | null>(
    () => (preview.data ? { srcDoc: emailReviewDocument(preview.data.html, pageUrl, reviewNonce()), label: title, loading, unavailable } : null),
    [preview.data, pageUrl, title, loading, unavailable],
  );

  useEffect(() => {
    if (reviewDocument || layout !== 'sheet') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeEmailReview();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reviewDocument, layout]);

  const finish = (message: string): string | null | false => {
    if (flow.sendComments) {
      const result = flow.sendComments(message);
      if (result.status !== 'sent') {
        setRefused(result);
        return false;
      }
      closeEmailReview();
      return conversationId;
    }
    closeEmailReview();
    openSideChat(conversationId ? { kind: 'existing', conversationId, message } : { kind: 'new', message, workspaceId });
    return conversationId;
  };

  if (!reviewDocument) {
    return (
      <div className={layout === 'pane' ? own.paneStatus : styles.body} role="status" data-testid="emailReviewStatus">
        <Typography variant="p2" color="primary">
          {preview.isError ? unavailable : loading}
        </Typography>
        <Button variant="outlined" onClick={closeEmailReview}>
          {t('siteReview.close')}
        </Button>
      </div>
    );
  }
  const openNewer = (newer: Extract<EmailReviewSendResult, { status: 'newer' }>) => {
    logPageClickBi({ page_name: 'chat', button_name: 'email_review_open_newer', ...(conversationId ? { conversation_id: conversationId } : {}) });
    setRefused(null);
    openEmailReview({ ...flow, card: newer.card, sendComments: newer.sendComments });
    void preview.refetch();
  };

  return (
    <div className={own.review}>
      {refused && (
        <div className={own.notice} role="status" data-testid="emailReviewRefused">
          <Typography variant="p3" color="primary">
            {t(`emailReview.refused.${refused.status}`)}
          </Typography>
          {refused.status === 'newer' && (
            <Button size="small" variant="outlined" onClick={() => openNewer(refused)} data-testid="emailReviewOpenNewer">
              {t('emailReview.refused.openNewer')}
            </Button>
          )}
        </div>
      )}
      <SiteReviewFrame
        previewUrl={pageUrl}
        reviewKey={emailReviewKey(card.campaignId, conversationId)}
        layout={layout}
        conversationId={conversationId}
        reviewDocument={reviewDocument}
        buildMessage={(comments) => emailReviewMessage({ metaSiteId: card.metaSiteId, campaignId: card.campaignId, subject }, comments)}
        onReload={() => void preview.refetch()}
        onFinish={finish}
        onClose={closeEmailReview}
      />
    </div>
  );
}
